import { sellerApiClient, retailApiClient } from './api';
import { authApi } from './authApi';

const APPROVAL_STATUS_VALUES = ['PENDING', 'APPROVED', 'REJECTED', 'SUSPENDED'] as const;
export type SellerApprovalStatus = typeof APPROVAL_STATUS_VALUES[number];

const PRODUCT_STATUS_VALUES = ['ACTIVE', 'INACTIVE', 'OUT_OF_STOCK'] as const;
export type SellerProductStatus = typeof PRODUCT_STATUS_VALUES[number];

const MODERATION_STATUS_VALUES = ['ACTIVE', 'PENDING_REVIEW', 'BLOCKED'] as const;
export type SellerModerationStatus = typeof MODERATION_STATUS_VALUES[number];

const ONBOARDING_STATUS_VALUES = ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'REJECTED'] as const;
export type SellerOnboardingStatus = typeof ONBOARDING_STATUS_VALUES[number];

const DOCUMENT_STATUS_VALUES = [
  'PENDING_UPLOAD',
  'UPLOADED',
  'VERIFIED',
  'REJECTED',
  'REQUEST_MORE_INFO',
  'REVOKED'
] as const;
export type SellerDocumentStatus = (typeof DOCUMENT_STATUS_VALUES)[number];

export interface SellerApplicationRequest {
  businessName: string;
  businessDescription?: string;
  businessAddress?: string;
  taxId?: string;
  contactEmail?: string;
  contactPhone?: string;
  country?: string;
}

export interface SellerProfile {
  sellerId: string;
  userId: string;
  businessName: string;
  businessDescription?: string | null;
  businessAddress?: string | null;
  taxId?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  approvalStatus: SellerApprovalStatus;
  approvalReason?: string | null;
  approvedBy?: string | null;
  approvedAt?: string | null;
  onboardingCompleted?: boolean;
  rating?: number | null;
  reviewCount?: number | null;
  ratingSource?: string | null;
  totalSales?: number | null;
  orderCount?: number | null;
  lastOrderAt?: string | null;
  lastOrderTotal?: number | null;
  supportEmail?: string | null;
  websiteUrl?: string | null;
  logoUrl?: string | null;
  businessAddressDetails?: SellerBusinessAddress | null;
  grievanceOfficer?: SellerGrievanceOfficer | null;
  hasTaxIdOnFile?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface SellerBusinessAddress {
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  region?: string | null;
  postalCode?: string | null;
  country?: string | null;
}

export interface SellerGrievanceOfficer {
  name?: string | null;
  designation?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface SellerProfileDetails {
  sellerId: string;
  businessDescription?: string | null;
  contactPhone?: string | null;
  supportEmail?: string | null;
  websiteUrl?: string | null;
  logoUrl?: string | null;
  taxId?: string | null;
  businessAddress?: SellerBusinessAddress | null;
  rating?: number | null;
  reviewCount?: number | null;
  ratingSource?: string | null;
  grievanceOfficer?: SellerGrievanceOfficer | null;
  hasTaxIdOnFile: boolean;
  updatedAt?: string;
}

export interface UpdateSellerProfileDetailsRequest {
  businessDescription?: string | null;
  contactPhone?: string | null;
  supportEmail?: string | null;
  websiteUrl?: string | null;
  logoUrl?: string | null;
  taxId?: string | null;
  businessAddress?: SellerBusinessAddress | null;
  grievanceOfficer?: SellerGrievanceOfficer | null;
}

export type SellerApplicationResponse = SellerProfile;

export interface SellerProduct {
  id: string;
  name: string;
  sku: string;
  price: number;
  stock: number;
  status: SellerProductStatus;
  moderationStatus?: SellerModerationStatus;
  blockedReason?: string | null;
  blockedAt?: string | null;
  listPrice?: number | null;
  discountAmount?: number | null;
  discountPercent?: number | null;
  shippingCharge?: number | null;
  returnFee?: number | null;
  restockingFee?: number | null;
  taxGroup?: string | null;
  tags?: string[];
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateProductRequest {
  name: string;
  sku: string;
  price: number;
  stock: number;
  listPrice?: number | null;
  discountAmount?: number | null;
  discountPercent?: number | null;
  shippingCharge?: number | null;
  returnFee?: number | null;
  restockingFee?: number | null;
  taxGroup?: string | null;
  tags?: string[];
}

export type UpdateProductRequest = Partial<CreateProductRequest> & {
  status?: SellerProductStatus;
  moderationStatus?: SellerModerationStatus;
  blockedReason?: string | null;
};

export interface SellerAnalyticsSummary {
  totalSales: number;
  orderCount: number;
  totalSkus: number;
  totalInventory: number;
  outOfStockSkus: number;
  returnedUnits: number;
  returnedValue: number;
}

export interface InventoryAnalytics {
  totalSkus: number;
  totalOnHand: number;
  totalReserved: number;
  outOfStockSkus: number;
}

export interface SellerRevenueDaily {
  date: string;
  revenue: number;
  orders: number;
}

export interface SellerRevenueSummary {
  startDate: string;
  endDate: string;
  grossRevenue: number;
  orders: number;
  unitsSold: number;
  averageOrderValue: number;
  daily: SellerRevenueDaily[];
}

export interface SellerFinanceSummary {
  totalSales: number;
  capturedSales: number;
  unsettledSales: number;
  orderCount: number;
  capturedOrderCount: number;
  returnedUnits: number;
  returnedValue: number;
  sellerCount: number;
  lastCaptureAt?: string | null;
  lastRefundAt?: string | null;
}

// KAN-808: seller settlement statement (retail-native adoption of the dhruvanta-settlement pattern).
// One payout the platform made to the seller, with how much of it was allocated to owed facts.
export interface SellerPayoutLine {
  payoutId: string;
  amount: number;
  allocatedAmount: number;
  currency: string;
  method?: string | null;
  externalReference?: string | null;
  notes?: string | null;
  occurredAt: string;
  recordedAt: string;
  allocationCount: number;
}

// The seller's settlement statement: owed / paid / outstanding totals plus the payout history. The same
// authoritative outstanding the finance endpoint's `unsettledSales` reports (KAN-808).
export interface SellerPayoutStatement {
  sellerId: string;
  owed: number;
  paid: number;
  outstanding: number;
  payouts: SellerPayoutLine[];
}

// KAN-776: per-item fulfilment lifecycle served by retail-core. Mirrors the backend
// FulfilmentStatus enum so the UI can drive actions from the server-provided allowedTransitions.
export const FULFILMENT_STATUS_VALUES = [
  'PENDING',
  'ACCEPTED',
  'PACKING',
  'SHIPPED',
  'DELIVERED',
  'RETURN_REQUESTED',
  'CANCELLED'
] as const;
export type FulfilmentStatus = (typeof FULFILMENT_STATUS_VALUES)[number];

export interface SellerOrderItem {
  orderItemId: string;
  productId: string;
  // Optional: retail-core returns productId; a human name is filled by the catalog when available.
  productName?: string;
  sellerProductId?: string | null;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  fulfilmentStatus: FulfilmentStatus;
  // The states this item can legally advance to next — drives the seller's action buttons.
  allowedTransitions: FulfilmentStatus[];
}

export interface SellerOrder {
  orderId: string;
  orderNumber: string;
  orderStatus: string;
  customerId?: string;
  // Optional: retail-core has no tax/shipping/payment breakdown today; the page tolerates absence.
  paymentStatus?: string;
  subtotal?: number;
  tax?: number;
  shipping?: number;
  total: number;
  currency: string;
  createdAt: string;
  updatedAt?: string;
  items: SellerOrderItem[];
}

export interface SellerOrdersPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface SellerOrdersResponse {
  orders: SellerOrder[];
  pagination: SellerOrdersPagination;
}

export interface SellerProductMedia {
  id: string;
  objectKey: string;
  primary: boolean;
  fileName: string;
  contentType: string;
  fileSize: number;
  createdAt: string;
}

export interface UploadProductImageOptions {
  primary?: boolean;
}

export interface SellerOnboardingStep {
  stepCode: string;
  status: SellerOnboardingStatus;
  notes?: string | null;
  completedAt?: string | null;
  updatedAt?: string | null;
}

export interface UpdateOnboardingStepRequest {
  stepCode: string;
  status: SellerOnboardingStatus;
  notes?: string;
}

export interface SellerDocument {
  documentId: string;
  documentType: string;
  storageKey: string | null;
  status: SellerDocumentStatus;
  uploadedAt: string | null;
  verifiedAt: string | null;
  rejectedReason: string | null;
}

export interface UpsertSellerDocumentRequest {
  documentType: string;
  storageKey: string;
}

export interface DocumentUploadUrlRequest {
  documentType: string;
  contentType: string;
  fileName: string;
}

export interface DocumentUploadUrlResponse {
  uploadUrl: string;
  storageKey: string;
  expiresAt?: string | null;
  headers?: Record<string, string> | null;
}

export interface UpdateSellerDocumentStatusRequest {
  status: SellerDocumentStatus;
  reason?: string;
}

// KAN-810: response of the seller KYC re-upload endpoint (mirrors the backend DocumentRegistrationResponse).
export interface ReuploadSellerDocumentResponse {
  documentId: string;
  documentType: string;
  status: SellerDocumentStatus;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  uploadUrl: string;
  uploadExpiresAt?: string | null;
}

// Document statuses a seller is allowed to re-upload against (reviewer-rejected states).
export const REUPLOADABLE_DOCUMENT_STATUSES: readonly SellerDocumentStatus[] = [
  'REJECTED',
  'REQUEST_MORE_INFO'
];

export const isDocumentReuploadable = (status: SellerDocumentStatus): boolean =>
  REUPLOADABLE_DOCUMENT_STATUSES.includes(status);

export type SellerStaffStatus = 'INVITED' | 'ACTIVE' | 'SUSPENDED' | 'REMOVED' | string;

export interface SellerStaff {
  staffId: string;
  userId?: string;
  email: string;
  name?: string;
  role?: string;
  permissions?: string[];
  status?: SellerStaffStatus;
  authScope?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateSellerStaffRequest {
  email: string;
  name: string;
  role: string;
  permissions?: string[];
  sendInvite?: boolean;
}

export interface CreateSellerStaffResponse {
  staff: SellerStaff;
  temporaryPassword?: string;
}

export interface UpdateSellerStaffRequest {
  permissions?: string[];
  status?: SellerStaffStatus;
}

export interface SellerInventoryItem {
  id: string;
  sku: string;
  productName: string;
  price: number;
  quantity: number;
  reservedQuantity?: number;
  status?: SellerProductStatus;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateInventoryItemRequest {
  sku: string;
  productName: string;
  price: number;
  quantity: number;
}

export interface UpdateInventoryItemRequest {
  price?: number;
  quantity?: number;
  status?: SellerProductStatus;
}

const generateFallbackId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `seller-${Math.random().toString(36).slice(2, 11)}`;
};

const coerceString = (value: unknown, fallback: string = ''): string => {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim();
  }
  if (typeof value === 'number') {
    return value.toString();
  }
  return fallback;
};

const coerceOptionalString = (value: unknown): string | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }
  const resolved = coerceString(value);
  return resolved.length > 0 ? resolved : undefined;
};

const coerceNullableString = (value: unknown): string | null => {
  if (value === null || value === undefined) {
    return null;
  }
  const resolved = coerceString(value);
  return resolved.length > 0 ? resolved : null;
};

const coerceNumber = (value: unknown, fallback: number = 0): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
};

const coerceNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const coerceStringArray = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const mapped = value
    .map(item => coerceString(item))
    .filter(item => item.length > 0);
  return mapped.length > 0 ? mapped : undefined;
};

const coerceRecord = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== 'object') {
    return {};
  }
  return value as Record<string, unknown>;
};

const normalizeBusinessAddress = (value: unknown): SellerBusinessAddress | null => {
  const source = coerceRecord(value);
  const address: SellerBusinessAddress = {
    addressLine1: coerceNullableString(source.addressLine1),
    addressLine2: coerceNullableString(source.addressLine2),
    city: coerceNullableString(source.city),
    region: coerceNullableString(source.region),
    postalCode: coerceNullableString(source.postalCode),
    country: coerceNullableString(source.country)
  };

  return Object.values(address).some(Boolean) ? address : null;
};

const formatBusinessAddress = (address?: SellerBusinessAddress | null): string | null => {
  if (!address) {
    return null;
  }

  const parts = [
    address.addressLine1,
    address.addressLine2,
    address.city,
    address.region,
    address.postalCode,
    address.country
  ].filter((item): item is string => typeof item === 'string' && item.trim().length > 0);

  return parts.length > 0 ? parts.join(', ') : null;
};

const normalizeGrievanceOfficer = (value: unknown): SellerGrievanceOfficer | null => {
  const source = coerceRecord(value);
  const grievanceOfficer: SellerGrievanceOfficer = {
    name: coerceNullableString(source.name),
    designation: coerceNullableString(source.designation),
    email: coerceNullableString(source.email),
    phone: coerceNullableString(source.phone)
  };

  return Object.values(grievanceOfficer).some(Boolean) ? grievanceOfficer : null;
};

const normalizeTextOrNull = (value: string | null | undefined): string | null => {
  if (typeof value !== 'string') {
    return value ?? null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const normalizeProductStatus = (value: unknown): SellerProductStatus => {
  if (typeof value === 'string') {
    const upper = value.toUpperCase() as SellerProductStatus;
    if (PRODUCT_STATUS_VALUES.includes(upper)) {
      return upper;
    }
  }
  return 'ACTIVE';
};

const normalizeModerationStatus = (value: unknown): SellerModerationStatus | undefined => {
  if (typeof value === 'string') {
    const upper = value.toUpperCase() as SellerModerationStatus;
    if (MODERATION_STATUS_VALUES.includes(upper)) {
      return upper;
    }
  }
  return undefined;
};

const normalizeDocumentStatus = (value: unknown): SellerDocumentStatus => {
  if (typeof value === 'string') {
    const upper = value.toUpperCase() as SellerDocumentStatus;
    if (DOCUMENT_STATUS_VALUES.includes(upper)) {
      return upper;
    }
  }
  return 'PENDING_UPLOAD';
};

const normalizeOnboardingStatus = (value: unknown): SellerOnboardingStatus => {
  if (typeof value === 'string') {
    const upper = value.toUpperCase() as SellerOnboardingStatus;
    if (ONBOARDING_STATUS_VALUES.includes(upper)) {
      return upper;
    }
  }
  return 'PENDING';
};

const buildProductPayload = (data: Partial<CreateProductRequest>): Record<string, unknown> => {
  const payload: Record<string, unknown> = {};
  if (data.name !== undefined) payload.name = data.name;
  if (data.sku !== undefined) payload.sku = data.sku;
  if (data.price !== undefined) payload.price = Number(data.price);
  if (data.stock !== undefined) payload.inventory = Number(data.stock); // V2 uses 'inventory'
  if (data.listPrice !== undefined) payload.listPrice = data.listPrice;
  if (data.discountAmount !== undefined) payload.discountAmount = data.discountAmount;
  if (data.discountPercent !== undefined) payload.discountPercent = data.discountPercent;
  if (data.shippingCharge !== undefined) payload.shippingCharge = data.shippingCharge;
  if (data.returnFee !== undefined) payload.returnFee = data.returnFee;
  if (data.restockingFee !== undefined) payload.restockingFee = data.restockingFee;
  if (data.taxGroup !== undefined) payload.taxGroup = data.taxGroup;
  if (data.tags !== undefined) payload.tags = data.tags;
  return payload;
};

const mapSellerProfile = (raw: unknown): SellerProfile => {
  const source = (raw && typeof raw === 'object') ? raw as Record<string, unknown> : {};
  const sellerId = coerceString(source.sellerId ?? source.id, generateFallbackId());
  const userId = coerceString(source.userId, generateFallbackId());

  // Approval status can come from legacy `approvalStatus` or v2 `status`.
  const approvalSource = typeof source.approvalStatus === 'string'
    ? source.approvalStatus
    : typeof source.status === 'string'
      ? source.status
      : 'PENDING';
  const upperApproval = approvalSource.toUpperCase();
  let approvalStatus: SellerApprovalStatus = 'PENDING';
  if (upperApproval === 'IN_PROGRESS') {
    approvalStatus = 'PENDING';
  } else if (APPROVAL_STATUS_VALUES.includes(upperApproval as SellerApprovalStatus)) {
    approvalStatus = upperApproval as SellerApprovalStatus;
  }

  return {
    sellerId,
    userId,
    // For v2 SellerResponse we only get legalName/displayName/primaryEmail.
    // Fall back accordingly so business info is visible even if legacy fields are missing.
    businessName: coerceString(
      source.businessName ?? source.legalName ?? source.displayName,
      'My Business'
    ),
    businessDescription: coerceNullableString(
      source.businessDescription ?? source.description
    ),
    businessAddress: coerceNullableString(
      source.businessAddress ?? source.address
    ),
    taxId: coerceNullableString(source.taxId),
    contactEmail: coerceNullableString(
      source.contactEmail ?? source.primaryEmail ?? source.email
    ),
    contactPhone: coerceNullableString(source.contactPhone),
    approvalStatus,
    approvalReason: coerceNullableString(source.approvalReason),
    approvedBy: coerceNullableString(source.approvedBy),
    approvedAt: coerceNullableString(source.approvedAt),
    onboardingCompleted: Boolean(source.onboardingCompleted),
    rating: coerceNullableNumber(source.rating),
    reviewCount: coerceNullableNumber(source.reviewCount),
    ratingSource: coerceNullableString(source.ratingSource),
    totalSales: coerceNullableNumber(source.totalSales),
    orderCount: coerceNullableNumber(source.orderCount),
    lastOrderAt: coerceNullableString(source.lastOrderAt),
    lastOrderTotal: coerceNullableNumber(source.lastOrderTotal),
    supportEmail: coerceNullableString(source.supportEmail),
    websiteUrl: coerceNullableString(source.websiteUrl),
    logoUrl: coerceNullableString(source.logoUrl),
    businessAddressDetails: normalizeBusinessAddress(source.businessAddress),
    grievanceOfficer: normalizeGrievanceOfficer(source.grievanceOfficer),
    hasTaxIdOnFile:
      typeof source.hasTaxIdOnFile === 'boolean'
        ? source.hasTaxIdOnFile
        : coerceNullableString(source.taxId) !== null,
    createdAt: coerceOptionalString(source.createdAt),
    updatedAt: coerceOptionalString(source.updatedAt)
  };
};

const mapSellerProfileDetails = (raw: unknown): SellerProfileDetails => {
  const source = coerceRecord(raw);
  const rating = coerceRecord(source.rating);

  return {
    sellerId: coerceString(source.sellerId ?? source.id, generateFallbackId()),
    businessDescription: coerceNullableString(source.businessDescription),
    contactPhone: coerceNullableString(source.contactPhone),
    supportEmail: coerceNullableString(source.supportEmail),
    websiteUrl: coerceNullableString(source.websiteUrl),
    logoUrl: coerceNullableString(source.logoUrl),
    taxId: coerceNullableString(source.taxId),
    businessAddress: normalizeBusinessAddress(source.businessAddress),
    rating: coerceNullableNumber(rating.averageRating),
    reviewCount: coerceNullableNumber(rating.reviewCount),
    ratingSource: coerceNullableString(rating.ratingSource),
    grievanceOfficer: normalizeGrievanceOfficer(source.grievanceOfficer),
    hasTaxIdOnFile: Boolean(source.hasTaxIdOnFile),
    updatedAt: coerceOptionalString(source.updatedAt)
  };
};

const mergeSellerProfile = (
  summary: SellerProfile,
  details: SellerProfileDetails
): SellerProfile => ({
  ...summary,
  businessDescription: details.businessDescription ?? summary.businessDescription ?? null,
  businessAddress: formatBusinessAddress(details.businessAddress) ?? summary.businessAddress ?? null,
  taxId: details.taxId ?? summary.taxId ?? null,
  contactPhone: details.contactPhone ?? summary.contactPhone ?? null,
  supportEmail: details.supportEmail ?? summary.supportEmail ?? null,
  websiteUrl: details.websiteUrl ?? summary.websiteUrl ?? null,
  logoUrl: details.logoUrl ?? summary.logoUrl ?? null,
  businessAddressDetails: details.businessAddress ?? summary.businessAddressDetails ?? null,
  grievanceOfficer: details.grievanceOfficer ?? summary.grievanceOfficer ?? null,
  hasTaxIdOnFile: details.hasTaxIdOnFile,
  rating: details.rating ?? summary.rating ?? null,
  reviewCount: details.reviewCount ?? summary.reviewCount ?? null,
  ratingSource: details.ratingSource ?? summary.ratingSource ?? null,
  updatedAt: details.updatedAt ?? summary.updatedAt
});

const buildProfileDetailsPayload = (
  current: SellerProfileDetails,
  updates: UpdateSellerProfileDetailsRequest
): Record<string, unknown> => {
  const mergedBusinessAddress = updates.businessAddress !== undefined
    ? {
        addressLine1:
          updates.businessAddress?.addressLine1 !== undefined
            ? normalizeTextOrNull(updates.businessAddress.addressLine1)
            : current.businessAddress?.addressLine1 ?? null,
        addressLine2:
          updates.businessAddress?.addressLine2 !== undefined
            ? normalizeTextOrNull(updates.businessAddress.addressLine2)
            : current.businessAddress?.addressLine2 ?? null,
        city:
          updates.businessAddress?.city !== undefined
            ? normalizeTextOrNull(updates.businessAddress.city)
            : current.businessAddress?.city ?? null,
        region:
          updates.businessAddress?.region !== undefined
            ? normalizeTextOrNull(updates.businessAddress.region)
            : current.businessAddress?.region ?? null,
        postalCode:
          updates.businessAddress?.postalCode !== undefined
            ? normalizeTextOrNull(updates.businessAddress.postalCode)
            : current.businessAddress?.postalCode ?? null,
        country:
          updates.businessAddress?.country !== undefined
            ? normalizeTextOrNull(updates.businessAddress.country)
            : current.businessAddress?.country ?? null
      }
    : {
        addressLine1: current.businessAddress?.addressLine1 ?? null,
        addressLine2: current.businessAddress?.addressLine2 ?? null,
        city: current.businessAddress?.city ?? null,
        region: current.businessAddress?.region ?? null,
        postalCode: current.businessAddress?.postalCode ?? null,
        country: current.businessAddress?.country ?? null
      };

  const mergedGrievanceOfficer = updates.grievanceOfficer !== undefined
    ? {
        name:
          updates.grievanceOfficer?.name !== undefined
            ? normalizeTextOrNull(updates.grievanceOfficer.name)
            : current.grievanceOfficer?.name ?? null,
        designation:
          updates.grievanceOfficer?.designation !== undefined
            ? normalizeTextOrNull(updates.grievanceOfficer.designation)
            : current.grievanceOfficer?.designation ?? null,
        email:
          updates.grievanceOfficer?.email !== undefined
            ? normalizeTextOrNull(updates.grievanceOfficer.email)
            : current.grievanceOfficer?.email ?? null,
        phone:
          updates.grievanceOfficer?.phone !== undefined
            ? normalizeTextOrNull(updates.grievanceOfficer.phone)
            : current.grievanceOfficer?.phone ?? null
      }
    : {
        name: current.grievanceOfficer?.name ?? null,
        designation: current.grievanceOfficer?.designation ?? null,
        email: current.grievanceOfficer?.email ?? null,
        phone: current.grievanceOfficer?.phone ?? null
      };

  return {
    businessDescription:
      updates.businessDescription !== undefined
        ? normalizeTextOrNull(updates.businessDescription)
        : current.businessDescription ?? null,
    contactPhone:
      updates.contactPhone !== undefined
        ? normalizeTextOrNull(updates.contactPhone)
        : current.contactPhone ?? null,
    supportEmail:
      updates.supportEmail !== undefined
        ? normalizeTextOrNull(updates.supportEmail)
        : current.supportEmail ?? null,
    websiteUrl:
      updates.websiteUrl !== undefined
        ? normalizeTextOrNull(updates.websiteUrl)
        : current.websiteUrl ?? null,
    logoUrl:
      updates.logoUrl !== undefined
        ? normalizeTextOrNull(updates.logoUrl)
        : current.logoUrl ?? null,
    taxId:
      updates.taxId !== undefined ? normalizeTextOrNull(updates.taxId) : current.taxId ?? null,
    averageRating: current.rating ?? null,
    reviewCount: current.reviewCount ?? null,
    ratingSource: current.ratingSource ?? null,
    businessAddress: mergedBusinessAddress,
    grievanceOfficer: mergedGrievanceOfficer
  };
};

const normalizeSellerProduct = (raw: unknown): SellerProduct => {
  const source = (raw && typeof raw === 'object') ? raw as Record<string, unknown> : {};
  const id = coerceString(source.id ?? source.productId, generateFallbackId());
  const name = coerceString(source.name ?? source.productName, `Listing ${id}`);
  const sku = coerceString(source.sku, 'SKU');
  const price = coerceNumber(source.price);
  const stock = coerceNumber(source.stock ?? source.inventory ?? source.quantity ?? 0);

  return {
    id,
    name,
    sku,
    price,
    stock,
    status: normalizeProductStatus(source.status),
    moderationStatus: normalizeModerationStatus(source.moderationStatus),
    blockedReason: coerceNullableString(source.blockedReason),
    blockedAt: coerceNullableString(source.blockedAt),
    listPrice: coerceNullableNumber(source.listPrice),
    discountAmount: coerceNullableNumber(source.discountAmount),
    discountPercent: coerceNullableNumber(source.discountPercent),
    shippingCharge: coerceNullableNumber(source.shippingCharge),
    returnFee: coerceNullableNumber(source.returnFee),
    restockingFee: coerceNullableNumber(source.restockingFee),
    taxGroup: coerceNullableString(source.taxGroup),
    tags: coerceStringArray(source.tags),
    createdAt: coerceOptionalString(source.createdAt),
    updatedAt: coerceOptionalString(source.updatedAt)
  };
};

const mapOnboardingStep = (raw: unknown): SellerOnboardingStep => {
  const source = (raw && typeof raw === 'object') ? raw as Record<string, unknown> : {};
  const stepCodeSource = source.stepCode ?? source.step;
  return {
    stepCode: coerceString(stepCodeSource, 'unknown'),
    status: normalizeOnboardingStatus(source.status),
    notes: coerceNullableString(source.notes),
    completedAt: coerceNullableString(source.completedAt),
    updatedAt: coerceNullableString(source.updatedAt)
  };
};

const mapSellerDocument = (raw: unknown): SellerDocument => {
  const source = (raw && typeof raw === 'object') ? raw as Record<string, unknown> : {};
  return {
    documentId: coerceString(source.documentId ?? source.id, generateFallbackId()),
    documentType: coerceString(source.documentType ?? source.type, 'UNKNOWN'),
    storageKey: coerceNullableString(source.storageKey ?? source.fileName),
    status: normalizeDocumentStatus(source.status),
    uploadedAt: coerceNullableString(source.uploadedAt),
    verifiedAt: coerceNullableString(source.verifiedAt),
    // The seller-service exposes the reviewer's rejection note as `adminComments`; older payloads
    // used `rejectedReason`. Carry whichever is present so the seller can see why a KYC doc was rejected.
    rejectedReason: coerceNullableString(source.rejectedReason ?? source.adminComments)
  };
};

const mapSellerStaff = (raw: unknown): SellerStaff => {
  const source = (raw && typeof raw === 'object') ? raw as Record<string, unknown> : {};
  return {
    staffId: coerceString(source.staffId ?? source.id, generateFallbackId()),
    userId: coerceOptionalString(source.userId),
    email: coerceString(source.email, 'unknown@invalid'),
    name: coerceOptionalString(source.name),
    role: coerceOptionalString(source.role),
    permissions: coerceStringArray(source.permissions) ?? [],
    status: coerceOptionalString(source.status),
    authScope: coerceOptionalString(source.authScope),
    createdAt: coerceOptionalString(source.createdAt),
    updatedAt: coerceOptionalString(source.updatedAt)
  };
};

const mapInventoryItem = (raw: unknown): SellerInventoryItem => {
  const source = (raw && typeof raw === 'object') ? raw as Record<string, unknown> : {};
  return {
    id: coerceString(source.id ?? source.inventoryItemId, generateFallbackId()),
    sku: coerceString(source.sku, 'SKU'),
    productName: coerceString(source.productName ?? source.name, 'Inventory Item'),
    price: coerceNumber(source.price),
    quantity: coerceNumber(source.quantity),
    reservedQuantity: coerceNullableNumber(source.reservedQuantity) ?? undefined,
    status: normalizeProductStatus(source.status),
    createdAt: coerceOptionalString(source.createdAt),
    updatedAt: coerceOptionalString(source.updatedAt)
  };
};

const arrayFrom = <T>(value: unknown, map: (raw: unknown) => T): T[] => {
  if (Array.isArray(value)) {
    return value.map(map);
  }
  if (value && typeof value === 'object') {
    const asObj = value as { items?: unknown[]; content?: unknown[] };
    if (Array.isArray(asObj.items)) {
      return asObj.items.map(map);
    }
    if (Array.isArray(asObj.content)) {
      return asObj.content.map(map);
    }
  }
  return [];
};

export const sellerApi = {
  /**
   * Submit or update a seller onboarding profile.
   */
  submitApplication: async (data: SellerApplicationRequest): Promise<SellerProfile> => {
    // V2 API expects different payload structure
    const payload = {
      legalName: data.businessName,
      displayName: data.businessName, // Fallback
      primaryEmail: data.contactEmail,
      country: data.country ?? 'IN',
      ...data
    };
    const response = await sellerApiClient.post<SellerProfile>('/v2/sellers', payload, true);
    return mapSellerProfile(response);
  },

  /**
   * Get seller profile (V2 Workaround).
   */
  getProfileV2: async (): Promise<SellerProfile> => {
    // Workaround: Backend /me endpoint is broken. We fetch sellers and filter by the current authenticated user.
    const currentUser = await authApi.getCurrentUser();
    const email = currentUser.email?.trim();
    const userId = currentUser.id?.trim();

    if (!email && !userId) {
      throw new Error('Authenticated user context is unavailable');
    }

    // Pass the email as a filter to the backend (verified to work in probe script)
    const params = new URLSearchParams();
    if (email) {
      params.set('primaryEmail', email);
    }

    if (import.meta.env.DEV) {
      console.log(`[SellerAPI] Looking up profile for ${email} with params: ${params.toString()}`);
    }
    const response = await sellerApiClient.get<unknown>(`/v2/sellers?${params.toString()}`, true);
    const candidates = arrayFrom(response, mapSellerProfile);

    // Try to find exact match
    const match = candidates.find(s =>
      (!!email && s.contactEmail && s.contactEmail.toLowerCase() === email.toLowerCase()) ||
      (!!userId && s.userId === userId)
    );

    if (match) return match;

    if (userId && userId !== email) {
      if (import.meta.env.DEV) {
        console.log(`[SellerAPI] Fallback lookup by userId: ${userId}`);
      }
      const userParams = new URLSearchParams();
      userParams.set('userId', userId);
      const userResp = await sellerApiClient.get<unknown>(`/v2/sellers?${userParams.toString()}`, true);
      const userCandidates = arrayFrom(userResp, mapSellerProfile);
      const userMatch = userCandidates.find(s => s.userId === userId);
      if (userMatch) return userMatch;
    }

    throw new Error('Seller profile not found for current user');
  },

  /**
   * Legacy getProfile (Unused)
   */
  getProfile: async (): Promise<SellerProfile> => {
    return sellerApi.getProfileV2();
  },

  getProfileDetails: async (sellerId: string): Promise<SellerProfileDetails> => {
    const response = await sellerApiClient.get<unknown>(`/v2/sellers/${sellerId}/profile`, true);
    return mapSellerProfileDetails(response);
  },

  getCurrentProfileDetails: async (): Promise<SellerProfileDetails> => {
    const summary = await sellerApi.getProfileV2();
    return sellerApi.getProfileDetails(summary.sellerId);
  },

  /**
   * Update seller profile details.
   */
  updateProfile: async (data: Partial<SellerApplicationRequest>): Promise<SellerProfile> => {
    const summary = await sellerApi.getProfileV2();
    const details = await sellerApi.updateProfileDetails(summary.sellerId, {
      businessDescription: data.businessDescription,
      contactPhone: data.contactPhone,
      supportEmail: data.contactEmail,
      taxId: data.taxId,
      businessAddress:
        data.businessAddress !== undefined
          ? { addressLine1: normalizeTextOrNull(data.businessAddress) }
          : undefined
    });
    return mergeSellerProfile(summary, details);
  },

  updateProfileDetails: async (
    sellerId: string,
    data: UpdateSellerProfileDetailsRequest
  ): Promise<SellerProfileDetails> => {
    const current = await sellerApi.getProfileDetails(sellerId);
    const payload = buildProfileDetailsPayload(current, data);
    const response = await sellerApiClient.put<unknown>(
      `/v2/sellers/${sellerId}/profile`,
      payload,
      true
    );
    return mapSellerProfileDetails(response);
  },

  /**
   * Create new product listing.
   */
  createProduct: async (data: CreateProductRequest): Promise<SellerProduct> => {
    // We need the Seller Profile ID for the V2 endpoint
    const profile = await sellerApi.getProfileV2();
    const sellerId = profile.sellerId;

    const payload = buildProductPayload(data);
    const response = await sellerApiClient.post<SellerProduct>(`/v2/sellers/${sellerId}/products`, payload, true);
    return normalizeSellerProduct(response);
  },

  /**
   * Get seller's products. Returns normalized pagination metadata.
   */
  getProducts: async (): Promise<{
    products: SellerProduct[];
    pagination: { page: number; size: number; total: number; totalPages: number };
  }> => {
    // V2: /v2/sellers/{sellerId}/products
    const profile = await sellerApi.getProfileV2();
    const sellerId = profile.sellerId;

    const response = await sellerApiClient.get<unknown>(`/v2/sellers/${sellerId}/products`, true);
    const products = arrayFrom(response, normalizeSellerProduct);
    const total = products.length;
    return {
      products,
      pagination: {
        page: 1,
        size: total,
        total,
        totalPages: 1
      }
    };
  },

  /**
   * Update product details.
   */
  updateProduct: async (productId: string | number, data: UpdateProductRequest): Promise<SellerProduct> => {
    // V2: /v2/sellers/{sellerId}/products/{productId}
    const profile = await sellerApi.getProfileV2();
    const sellerId = profile.sellerId;

    const payload = {
      ...buildProductPayload(data),
      ...(data.status ? { status: data.status } : {}),
      ...(data.moderationStatus ? { moderationStatus: data.moderationStatus } : {}),
      ...(data.blockedReason !== undefined ? { blockedReason: data.blockedReason } : {})
    };
    const response = await sellerApiClient.put<SellerProduct>(`/v2/sellers/${sellerId}/products/${String(productId)}`, payload, true);
    return normalizeSellerProduct(response);
  },

  /**
   * Remove product listing.
   */
  /**
   * Remove product listing.
   */
  deleteProduct: async (productId: string | number): Promise<void> => {
    // V2: /v2/sellers/{sellerId}/products/{productId}
    const profile = await sellerApi.getProfileV2();
    const sellerId = profile.sellerId;
    await sellerApiClient.delete(`/v2/sellers/${sellerId}/products/${String(productId)}`, true);
  },

  /**
   * Upload a product image (<=10MB, jpg/png/gif/webp). Optionally mark as primary.
   */
  uploadProductImage: async (
    productId: string | number,
    file: File | Blob,
    options?: UploadProductImageOptions
  ): Promise<SellerProductMedia> => {
    // V2: /v2/sellers/{sellerId}/products/{productId}/images
    const profile = await sellerApi.getProfileV2();
    const sellerId = profile.sellerId;
    const productKey = String(productId);

    const formData = new FormData();
    formData.append('file', file);

    if (options?.primary) {
      formData.append('primary', 'true');
    }

    return sellerApiClient.postForm<SellerProductMedia>(`/v2/sellers/${sellerId}/products/${productKey}/images`, formData, true);
  },

  /**
   * List images for a product.
   */
  listProductImages: async (productId: string | number): Promise<SellerProductMedia[]> => {
    // V2: /v2/sellers/{sellerId}/products/{productId}/images
    const profile = await sellerApi.getProfileV2();
    const sellerId = profile.sellerId;
    const productKey = String(productId);

    return sellerApiClient.get<SellerProductMedia[]>(`/v2/sellers/${sellerId}/products/${productKey}/images`, true);
  },

  /**
   * Delete a product image via its object key.
   */
  deleteProductImage: async (productId: string | number, objectKey: string): Promise<void> => {
    // V2: /v2/sellers/{sellerId}/products/{productId}/images
    const profile = await sellerApi.getProfileV2();
    const sellerId = profile.sellerId;
    const productKey = String(productId);
    const params = new URLSearchParams({ objectKey });

    await sellerApiClient.delete<void>(`/v2/sellers/${sellerId}/products/${productKey}/images?${params.toString()}`, true);
  },

  /**
   * Set a specific product image as primary.
   */
  setPrimaryProductImage: async (productId: string | number, objectKey: string): Promise<void> => {
    // V2: /v2/sellers/{sellerId}/products/{productId}/images/primary
    const profile = await sellerApi.getProfileV2();
    const sellerId = profile.sellerId;
    const productKey = String(productId);

    await sellerApiClient.post<void>(
      `/v2/sellers/${sellerId}/products/${productKey}/images/primary`,
      { objectKey },
      true
    );
  },

  /**
   * Generate a presigned URL for a stored product image.
   */
  presignProductImage: async (
    productId: string | number,
    objectKey: string,
    expirySeconds: number = 900
  ): Promise<string> => {
    // V2: /v2/sellers/{sellerId}/products/{productId}/images/presign
    const profile = await sellerApi.getProfileV2();
    const sellerId = profile.sellerId;
    const productKey = String(productId);

    const params = new URLSearchParams({
      objectKey,
      expiry: expirySeconds.toString()
    });
    const raw = await sellerApiClient.get<unknown>(
      `/v2/sellers/${sellerId}/products/${productKey}/images/presign?${params.toString()}`,
      true
    );

    if (typeof raw === 'string') {
      return raw;
    }

    if (raw && typeof raw === 'object') {
      const payload = raw as Record<string, unknown>;
      const candidate =
        (typeof payload.url === 'string' && payload.url) ||
        (typeof payload.downloadUrl === 'string' && payload.downloadUrl) ||
        (typeof payload.imageUrl === 'string' && payload.imageUrl);

      if (candidate) {
        return candidate;
      }
    }

    throw new Error('Unexpected response from image presign endpoint');
  },

  /**
   * Get seller analytics summary (orders, inventory, returns).
   */
  getAnalyticsSummary: async (): Promise<SellerAnalyticsSummary> => {
    // V2: /v2/sellers/{sellerId}/analytics
    const profile = await sellerApi.getProfileV2();
    const sellerId = profile.sellerId;
    return sellerApiClient.get<SellerAnalyticsSummary>(`/v2/sellers/${sellerId}/analytics`, true);
  },

  /**
   * Get high-level finance metrics.
   */
  getFinanceMetrics: async (): Promise<SellerFinanceSummary> => {
    // V2: /v2/sellers/{sellerId}/analytics/finance
    const profile = await sellerApi.getProfileV2();
    const sellerId = profile.sellerId;
    return sellerApiClient.get<SellerFinanceSummary>(`/v2/sellers/${sellerId}/analytics/finance`, true);
  },

  /**
   * Get the seller settlement statement (KAN-808): owed / paid / outstanding totals + payout history.
   * Served by retail-core (owns the order data + the settlement ledger), seller-scoped to the caller.
   * GET /api/v2/sellers/{sellerId}/settlement/statement
   */
  getSettlementStatement: async (): Promise<SellerPayoutStatement> => {
    const profile = await sellerApi.getProfileV2();
    const sellerId = profile.sellerId;
    return retailApiClient.get<SellerPayoutStatement>(
      `/v2/sellers/${sellerId}/settlement/statement`,
      true
    );
  },

  /**
   * Get inventory analytics for the current seller.
   */
  getInventoryAnalytics: async (): Promise<InventoryAnalytics> => {
    // V2: /v2/sellers/{sellerId}/inventory/analytics
    // Some environments may not have this endpoint wired yet; fall back to a stub
    // so the dashboard doesn't break if it's missing.
    const profile = await sellerApi.getProfileV2();
    const sellerId = profile.sellerId;
    try {
      return await sellerApiClient.get<InventoryAnalytics>(
        `/v2/sellers/${sellerId}/inventory/analytics`,
        true
      );
    } catch (err) {
      console.warn(
        '[SellerAPI] Inventory analytics endpoint unavailable, returning empty metrics',
        err
      );
      return {
        totalSkus: 0,
        totalOnHand: 0,
        totalReserved: 0,
        outOfStockSkus: 0
      };
    }
  },

  /**
   * Get revenue summary for an optional date range.
   */
  getRevenueSummary: async (params?: { startDate?: string; endDate?: string }): Promise<SellerRevenueSummary> => {
    // V2: /v2/sellers/{sellerId}/analytics/revenue
    const profile = await sellerApi.getProfileV2();
    const sellerId = profile.sellerId;

    const search = new URLSearchParams();
    if (params?.startDate) {
      search.set('startDate', params.startDate);
    }
    if (params?.endDate) {
      search.set('endDate', params.endDate);
    }
    const query = search.toString();
    const endpoint = query ? `/v2/sellers/${sellerId}/analytics/revenue?${query}` : `/v2/sellers/${sellerId}/analytics/revenue`;
    return sellerApiClient.get<SellerRevenueSummary>(endpoint, true);
  },

  /**
   * Get seller's orders with pagination and optional status filter.
   */
  getOrders: async (
    page: number = 1,
    limit: number = 20,
    status?: string
  ): Promise<SellerOrdersResponse> => {
    // KAN-776: served by retail-core (owns the order data), seller-scoped to the caller's items.
    // GET /api/v2/sellers/{sellerId}/orders
    const profile = await sellerApi.getProfileV2();
    const sellerId = profile.sellerId;

    const params = new URLSearchParams({
      page: Math.max(1, page).toString(),
      limit: Math.max(1, limit).toString()
    });
    if (status) {
      params.set('status', status);
    }
    return retailApiClient.get<SellerOrdersResponse>(`/v2/sellers/${sellerId}/orders?${params.toString()}`, true);
  },

  /**
   * Advance a single order item through the fulfilment state machine (KAN-776).
   * Authorization- and state-machine-validated server-side: an illegal jump or another seller's
   * item is rejected. POST /api/v2/sellers/{sellerId}/orders/{orderId}/items/{itemId}/fulfilment
   */
  advanceFulfilment: async (
    orderId: string,
    orderItemId: string,
    status: FulfilmentStatus
  ): Promise<SellerOrderItem> => {
    const profile = await sellerApi.getProfileV2();
    const sellerId = profile.sellerId;
    return retailApiClient.post<SellerOrderItem>(
      `/v2/sellers/${sellerId}/orders/${orderId}/items/${orderItemId}/fulfilment`,
      { status },
      true
    );
  },

  /**
   * Fetch onboarding steps for the seller.
   */
  listOnboardingSteps: async (): Promise<SellerOnboardingStep[]> => {
    // V2: /v2/sellers/{sellerId}/workflow/steps
    const profile = await sellerApi.getProfileV2();
    const sellerId = profile.sellerId;
    const response = await sellerApiClient.get<unknown>(`/v2/sellers/${sellerId}/workflow/steps`, true);

    // Backend returns SellerOnboardingProgressResponse: { workflowInstanceId, steps: Step[] }
    const responseRecord = coerceRecord(response);
    const stepsSource = Array.isArray(responseRecord.steps)
      ? responseRecord.steps
      : Array.isArray(response)
        ? response
        : [];

    return arrayFrom(stepsSource, mapOnboardingStep);
  },

  /**
   * Update a specific onboarding step status.
   */
  updateOnboardingStep: async (payload: UpdateOnboardingStepRequest): Promise<SellerOnboardingStep> => {
    // V2: /v2/sellers/{sellerId}/workflow/steps (status update)
    const profile = await sellerApi.getProfileV2();
    const sellerId = profile.sellerId;
    const response = await sellerApiClient.post<SellerOnboardingStep>(
      `/v2/sellers/${sellerId}/workflow/steps`,
      payload,
      true
    );
    return mapOnboardingStep(response);
  },

  /**
   * List uploaded compliance documents.
   */
  listDocuments: async (): Promise<SellerDocument[]> => {
    // V2: /v2/sellers/{sellerId}/documents
    const profile = await sellerApi.getProfileV2();
    const sellerId = profile.sellerId;
    const response = await sellerApiClient.get<unknown>(`/v2/sellers/${sellerId}/documents`, true);
    return arrayFrom(response, mapSellerDocument);
  },

  /**
   * Upsert (create or update) a compliance document reference.
   */
  upsertDocument: async (payload: UpsertSellerDocumentRequest): Promise<SellerDocument> => {
    // V2: /v2/sellers/{sellerId}/documents
    const profile = await sellerApi.getProfileV2();
    const sellerId = profile.sellerId;
    const response = await sellerApiClient.post<SellerDocument>(`/v2/sellers/${sellerId}/documents`, payload, true);
    return mapSellerDocument(response);
  },

  /**
   * Create a presigned upload URL for a seller document.
   */
  createDocumentUploadUrl: async (
    payload: DocumentUploadUrlRequest
  ): Promise<DocumentUploadUrlResponse> => {
    // V2: /v2/sellers/{sellerId}/documents/upload-url
    const profile = await sellerApi.getProfileV2();
    const sellerId = profile.sellerId;
    return sellerApiClient.post<DocumentUploadUrlResponse>(
      `/v2/sellers/${sellerId}/documents/upload-url`,
      payload,
      true
    );
  },

  /**
   * Update document status (admin or delegated approval flow).
   */
  updateDocumentStatus: async (
    sellerId: string,
    documentType: string,
    payload: UpdateSellerDocumentStatusRequest
  ): Promise<SellerDocument> => {
    const response = await sellerApiClient.put<SellerDocument>(
      `/v2/sellers/${sellerId}/approvals/documents/${documentType}`,
      payload,
      true
    );
    return mapSellerDocument(response);
  },

  /**
   * Re-upload a previously rejected KYC document (KAN-810). Only documents the reviewer marked
   * REJECTED or REQUEST_MORE_INFO can be re-uploaded; the backend supersedes the rejected record and
   * places the replacement back into the admin review queue. Registers the replacement, then PUTs the
   * corrected file to the returned presigned upload URL.
   */
  reuploadDocument: async (documentId: string, file: File | Blob): Promise<ReuploadSellerDocumentResponse> => {
    const profile = await sellerApi.getProfileV2();
    const sellerId = profile.sellerId;

    const fileName = file instanceof File && file.name ? file.name : 'document';
    const response = await sellerApiClient.post<ReuploadSellerDocumentResponse>(
      `/v2/sellers/${sellerId}/documents/${documentId}/reupload`,
      {
        fileName,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size
      },
      true
    );

    if (response?.uploadUrl) {
      const uploadResponse = await fetch(response.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file
      });
      if (!uploadResponse.ok) {
        throw new Error(`Document re-upload failed with status ${uploadResponse.status}`);
      }
    }

    return response;
  },

  /**
   * List staff members for the seller workspace.
   */
  listStaff: async (): Promise<SellerStaff[]> => {
    // V2: /v2/sellers/{sellerId}/staff
    const profile = await sellerApi.getProfileV2();
    const sellerId = profile.sellerId;
    const response = await sellerApiClient.get<unknown>(`/v2/sellers/${sellerId}/staff`, true);
    return arrayFrom(response, mapSellerStaff);
  },

  /**
   * Invite or create a new staff member.
   */
  createStaff: async (payload: CreateSellerStaffRequest): Promise<CreateSellerStaffResponse> => {
    // V2: /v2/sellers/{sellerId}/staff
    const profile = await sellerApi.getProfileV2();
    const sellerId = profile.sellerId;
    const response = await sellerApiClient.post<CreateSellerStaffResponse>(`/v2/sellers/${sellerId}/staff`, payload, true);
    return {
      staff: mapSellerStaff(response.staff),
      temporaryPassword: response.temporaryPassword ?? undefined
    };
  },

  /**
   * Update staff permissions or status.
   */
  updateStaff: async (staffId: string, payload: UpdateSellerStaffRequest): Promise<SellerStaff> => {
    // V2: /v2/sellers/{sellerId}/staff/{staffId}
    const profile = await sellerApi.getProfileV2();
    const sellerId = profile.sellerId;
    const response = await sellerApiClient.patch<SellerStaff>(`/v2/sellers/${sellerId}/staff/${staffId}`, payload, true);
    return mapSellerStaff(response);
  },

  /**
   * List inventory items managed by the seller.
   */
  listInventoryItems: async (): Promise<SellerInventoryItem[]> => {
    // V2: /v2/sellers/{sellerId}/inventory
    const profile = await sellerApi.getProfileV2();
    const sellerId = profile.sellerId;
    const response = await sellerApiClient.get<unknown>(`/v2/sellers/${sellerId}/inventory`, true);
    return arrayFrom(response, mapInventoryItem);
  },

  /**
   * Create a new inventory item entry.
   */
  createInventoryItem: async (payload: CreateInventoryItemRequest): Promise<SellerInventoryItem> => {
    // V2: /v2/sellers/{sellerId}/inventory
    const profile = await sellerApi.getProfileV2();
    const sellerId = profile.sellerId;
    const response = await sellerApiClient.post<SellerInventoryItem>(`/v2/sellers/${sellerId}/inventory`, payload, true);
    return mapInventoryItem(response);
  },

  /**
   * Update inventory item quantity, price, or status.
   */
  updateInventoryItem: async (
    itemId: string,
    payload: UpdateInventoryItemRequest
  ): Promise<SellerInventoryItem> => {
    // V2: /v2/sellers/{sellerId}/inventory/{itemId}
    const profile = await sellerApi.getProfileV2();
    const sellerId = profile.sellerId;
    const response = await sellerApiClient.patch<SellerInventoryItem>(`/v2/sellers/${sellerId}/inventory/${itemId}`, payload, true);
    return mapInventoryItem(response);
  }
};

export const sellerApiInternals = {
  mapSellerProfile,
  normalizeSellerProduct,
  mapOnboardingStep,
  mapSellerDocument,
  mapSellerStaff,
  mapInventoryItem,
  buildProductPayload,
  arrayFrom,
  coerceNumber,
  coerceNullableNumber,
  coerceString,
  coerceOptionalString,
  coerceNullableString
};
